import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';

export interface SearchSelectOption {
  /** Stable id handed back to onChange. */
  value: string;
  /** Primary text shown in the field and as the option's first line. */
  label: string;
  /** Secondary muted line under the label (e.g. a SKU). */
  sublabel?: string;
  /** Extra text folded into the search match but never displayed. */
  keywords?: string;
}

interface Props {
  options: SearchSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Shown when the typed query matches no option. */
  emptyText?: string;
  disabled?: boolean;
  /** Applied to the wrapper — use it to set the field width. */
  className?: string;
  id?: string;
}

interface MenuPos {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

const MENU_MAX = 256; // matches max-h-64
const GAP = 4;

/**
 * A searchable single-select combobox: type to filter a long option list,
 * arrow keys + Enter to choose, Escape / outside-click to close. Use in place of
 * a native <select> when the list is long enough that scanning it is painful
 * (e.g. the 500+ product catalogue). Generalises the inline driver picker that
 * used to live in ShopHandoverPage.
 *
 * The dropdown is portalled to <body> with fixed positioning (and flips above
 * the field when there's more room there), so it never gets clipped by an
 * `overflow` ancestor — it works inside scrolling Modals and tables, not just
 * on a plain page.
 *
 * Fully client-side — pass it the whole option list. The displayed text always
 * reflects the externally-controlled `value`, so resetting `value` to '' (e.g.
 * after a successful submit) clears the field.
 */
export function SearchSelect({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  emptyText = 'No match',
  disabled,
  className,
  id,
}: Props): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [pos, setPos] = useState<MenuPos | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      `${o.label} ${o.sublabel ?? ''} ${o.keywords ?? ''}`.toLowerCase().includes(q),
    );
  }, [options, query]);

  // Position the portalled menu against the input, flipping up when the space
  // below is too small. Recomputed on open and on any scroll/resize while open.
  useLayoutEffect(() => {
    if (!open) return;
    const place = (): void => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      const above = r.top;
      const up = below < Math.min(MENU_MAX, 200) && above > below;
      setPos({
        left: r.left,
        width: r.width,
        maxHeight: Math.max(96, Math.min(MENU_MAX, (up ? above : below) - GAP - 4)),
        ...(up ? { bottom: window.innerHeight - r.top + GAP } : { top: r.bottom + GAP }),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Close on a click outside both the input and the (portalled) menu.
  useEffect(() => {
    if (!open) return;
    function onDocDown(e: MouseEvent): void {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || listRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open || activeIdx < 0) return;
    const el = listRef.current?.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIdx, open]);

  const pick = (o: SearchSelectOption): void => {
    onChange(o.value);
    setQuery('');
    setOpen(false);
    setActiveIdx(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (filtered.length === 0 ? -1 : (i + 1) % filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? filtered.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0 && filtered[activeIdx]) {
      e.preventDefault();
      pick(filtered[activeIdx]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  // Closed → show the selected option's label; open → show the live query.
  const display = open ? query : selected?.label ?? '';

  return (
    <div className={cn('relative', className)}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="kc-input w-full"
        placeholder={placeholder}
        value={display}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-controls={id ? `${id}-listbox` : undefined}
        autoComplete="off"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIdx(-1);
        }}
        onFocus={(e) => {
          if (disabled) return;
          setOpen(true);
          setQuery('');
          e.target.select();
        }}
        onKeyDown={onKeyDown}
      />
      {open && !disabled && pos &&
        createPortal(
          <div
            ref={listRef}
            id={id ? `${id}-listbox` : undefined}
            className="fixed z-[60] bg-surface border border-rule rounded-md shadow-lg overflow-y-auto"
            style={{
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
              ...(pos.top != null ? { top: pos.top } : {}),
              ...(pos.bottom != null ? { bottom: pos.bottom } : {}),
            }}
            role="listbox"
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-ink-3 italic">{emptyText}</div>
            ) : (
              filtered.map((o, i) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => pick(o)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={cn(
                    'block w-full text-left px-3 py-2 text-sm border-b border-rule-2 last:border-b-0 transition',
                    o.value === value
                      ? 'bg-accent/10 text-ink'
                      : i === activeIdx
                        ? 'bg-paper-2 text-ink'
                        : 'text-ink-2 hover:bg-paper-2 hover:text-ink',
                  )}
                  role="option"
                  aria-selected={o.value === value}
                >
                  <span className="block">{o.label}</span>
                  {o.sublabel && <span className="block text-xs text-ink-3 kc-mono">{o.sublabel}</span>}
                </button>
              ))
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
